import { useState, useEffect } from "react"
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { BASE_URL } from '../../../apiConfig';
import Loading from '../../../cors/Loading';
import { toast } from 'sonner';
import PaymentList from '../../../elements/saller_payment_list'; 
import SallerExportExcel from "../../../cors/SallerExportExcel";
import { Clock, ChevronLeft, ChevronRight, Info, FileText } from 'lucide-react';
import Select from "react-select";
import { MdOutlineFileDownloadDone , MdAttachMoney } from "react-icons/md";
import OzonInvoiceImporter from "../../../components/modals/Ozoninvoiceimporter";

export default function AdminSellerPayments() {
  const { t } = useLanguage();
  const { token, role, roles } = useAuth();
  const PER_PAGE_OPTIONS = [
    { value: 20, label: "20" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
  ];
  const [searchTerm, setSearchTerm] = useState("")
  const [showExportModal, setShowExportModal] = useState(false);
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [showOzonImporter, setShowOzonImporter] = useState(false);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
    totalBenefice: 0,
  });
  function CardWave({ color }) {
  return (
    <svg
      className="absolute bottom-0 left-0 w-full h-10 opacity-70"
      viewBox="0 0 300 60"
      preserveAspectRatio="none"
    >
      <path
        d="M0,40 C60,10 90,55 150,35 C210,15 250,50 300,25 L300,60 L0,60 Z"
        fill={color}
      />
    </svg>
  );
}

  function HeroIllustration() {
    return (
      <svg viewBox="0 0 220 160" className="w-44 h-32 md:w-56 md:h-40" aria-hidden="true">
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* soft ground shadow */}
        <ellipse cx="110" cy="148" rx="80" ry="8" fill="#0D9488" opacity="0.08" />

        {/* scattered dots, top right */}
        <circle cx="190" cy="18" r="3" fill="#99F0DE" />
        <circle cx="200" cy="30" r="2" fill="#99F0DE" />
        <circle cx="178" cy="8" r="2" fill="#99F0DE" />

        {/* tilted background card */}
        <g transform="rotate(-4 115 60)">
          <rect x="55" y="8" width="122" height="98" rx="20" fill="#CFF3E8" />
        </g>

        {/* main white panel */}
        <rect x="68" y="24" width="96" height="68" rx="12" fill="#FFFFFF" stroke="#E3F6F0" strokeWidth="1" />

        {/* window dots */}
        <circle cx="82" cy="37" r="3" fill="#F87171" />
        <circle cx="91" cy="37" r="3" fill="#FBBF24" />
        <circle cx="100" cy="37" r="3" fill="#34D399" />

        {/* header bars */}
        <rect x="80" y="48" width="30" height="5" rx="2.5" fill="#0F766E" />
        <rect x="80" y="57" width="18" height="4" rx="2" fill="#99F0DE" />

        {/* chart with filled area + highlighted point */}
        <path d="M78,86 L78,72 L90,60 L102,68 L116,48 L152,58 L152,86 Z" fill="url(#chartFill)" />
        <polyline
          points="78,72 90,60 102,68 116,48 152,58"
          fill="none"
          stroke="#14B8A6"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="116" cy="48" r="3.5" fill="#FFFFFF" stroke="#0D9488" strokeWidth="2.5" />

        {/* floating mini stat card */}
        <g>
          <rect x="8" y="72" width="66" height="46" rx="14" fill="#0D9488" />
          <rect x="18" y="86" width="26" height="6" rx="3" fill="#CFF3E8" />
          <rect x="18" y="97" width="40" height="5" rx="2.5" fill="#5EEAD4" />
          <path d="M50,84 L58,84 L58,100" fill="none" stroke="#5EEAD4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M46,96 L52,90 L58,94 L64,84" fill="none" stroke="#F5C87A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* coin stack */}
        <g>
          <ellipse cx="152" cy="132" rx="17" ry="6" fill="#C9862F" />
          <rect x="135" y="122" width="34" height="10" fill="#EDB35F" />
          <ellipse cx="152" cy="122" rx="17" ry="6" fill="#F5C87A" />
          <ellipse cx="152" cy="122" rx="10" ry="3.5" fill="#FBDFA6" />
        </g>
        <g>
          <ellipse cx="172" cy="120" rx="13" ry="5" fill="#C9862F" />
          <rect x="159" y="112" width="26" height="8" fill="#EDB35F" />
          <ellipse cx="172" cy="112" rx="13" ry="5" fill="#F5C87A" />
          <ellipse cx="172" cy="112" rx="7.5" ry="2.8" fill="#FBDFA6" />
        </g>
      </svg>
    );
  }


  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchInvoices(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const fetchInvoices = async (page = 1, search = searchTerm, perPageOverride = null) => {
    try {
      setLoading(true);
      const effectivePerPage =
        perPageOverride !== null && perPageOverride !== undefined
          ? perPageOverride
          : perPage;
      const res = await fetch(`${BASE_URL}/api/sallers-invoices?page=${page}&search=${search}&per_page=${effectivePerPage}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setInvoices(data.invoices.data);  
        setCurrentPage(data.invoices.current_page);
        setLastPage(data.invoices.last_page);
        setPerPage(data.invoices.per_page);
        setTotal(data.invoices.total);
        setStats(data.stats); 
        setError(null);
      }
    } catch (err) {
      console.error(err);
      setError(t('payments.error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const pagination = {
    currentPage,
    lastPage,
    total,
    from: (currentPage - 1) * perPage + 1,
    to: Math.min(currentPage * perPage, total)
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleCloseModal = () => {
    setShowExportModal(false);
  };

  const handleGeneratePayment = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/saller-auto-invoices`, {
        method: 'GET',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json' 
        }
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(data.message);
        fetchInvoices();
      } else {
        toast.warning(data.message);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('payments.error_occurred'));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      fetchInvoices(newPage);
    }
  };

  const prevPage = () => handlePageChange(currentPage - 1);
  const nextPage = () => handlePageChange(currentPage + 1);
  const paginate = (pageNum) => handlePageChange(pageNum);

  const handlePerPageChange = (opt) => {
    const next = opt?.value ?? 20;
    setPerPage(next);
    setLoading(true);
    fetchInvoices(1, searchTerm, next);
  };

  const { totalInvoices, pendingInvoices, paidInvoices, totalBenefice } = stats;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-5">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-0">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('payments.sellers_title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('payments.subtitle')}
            </p>
          </div>
          <div className="hidden md:block">
              <HeroIllustration />
          </div>
          {
            role === 1 || (role === 2 && roles.generate_saller_invoices === 1) ? (
              <div className="mt-6 md:mt-0 flex gap-4">
                <button 
                  onClick={() => setShowOzonImporter(true)} 
                  className="cursor-pointer text-xs bg-[#f78c21] hover:bg-[#f78c21]/80 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300">
                    {t('payments.import_ozon')}
                </button>
                <button 
                onClick={handleExportClick} 
                className="cursor-pointer text-xs bg-emerald-500 hover:bg-emerald-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300">
                  {t('payments.export_excel')}
                </button>
                {showExportModal && (
                  <SallerExportExcel 
                    onClose={handleCloseModal}
                  />
                )}
                <button 
                  onClick={handleGeneratePayment}
                  className="cursor-pointer text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300"
                >
                  {t('payments.generate')}
                </button>
              </div>
            ): ''
          }
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.total_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-teal-600">{totalInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-teal-600" />
              </div>
            </div>
            <CardWave color="#CCFBF1" />
          </div>

          {/* Pending invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.pending_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-yellow-600">{pendingInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <CardWave color="#FEF3C7" />
          </div>

          {/* Paid invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.paid_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-green-600">{paidInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MdOutlineFileDownloadDone className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <CardWave color="#D1FAE5" />
          </div>

          {/* Total amount */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.total_amount')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-2xl font-bold text-blue-600">{totalBenefice != null ? totalBenefice.toFixed(2) : 0} MAD</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MdAttachMoney className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <CardWave color="#DBEAFE" />
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-8">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('payments.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>
          
          </div>
        </div>

        {/* État de chargement */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loading />
          </div>
        )}

        {/* État d'erreur */}
        {error && <p className="text-center text-red-500 py-10">{error}</p>}

        {/* Affichage des factures */}
        {!loading && !error && (
          <>
            {/* Compteur de résultats */}
            <div className="mb-8 flex justify-between items-center">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-xs font-medium">
                {invoices.length} {invoices.length === 1 ? t('payments.invoice_singular') : t('payments.invoice_plural')}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-gray-500">{t('payments.per_page')}</span>
                <Select
                  instanceId="sallers-table-per-page"
                  options={PER_PAGE_OPTIONS}
                  value={PER_PAGE_OPTIONS.find((o) => o.value === perPage) ?? PER_PAGE_OPTIONS[0]}
                  onChange={handlePerPageChange}
                  isSearchable={false}
                  className="text-xs min-w-[4.5rem]"
                  classNamePrefix="sallers-per-page"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: "10px",
                      minHeight: "32px",
                      minWidth: "4.5rem",
                      outline: "none",
                    }),
                    dropdownIndicator: (provided) => ({ ...provided, padding: 4 }),
                    valueContainer: (provided) => ({ ...provided, padding: "0px 10px" }),
                  }}
                />
              </div>
            </div>

            {/* Liste des factures */}
            {invoices.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-x-auto custom-scrollbar">
                  <div className="min-w-[60rem]">
                    <table className="w-full ">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.invoice')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.seller')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.bank')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.orders')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.amount')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.payment_date')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.status')}</th>
                        <th className="px-1 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((invoice,key) => (
                        <PaymentList key={key} invoice={invoice} role={role} fetchInvoices={fetchInvoices} />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              
            ) : (
              /* État vide */
              <div className="text-center py-1">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('payments.not_found')}</h3>
                {
                  role === 1 &&
                  <div>
                    <p className="text-gray-600 text-sm mb-6">{t('payments.not_found_desc')}</p>
                    <button
                      onClick={handleGeneratePayment}
                      className="cursor-pointer text-xs bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold py-2 px-6 rounded-xl transition-all duration-300"
                    >
                      {t('payments.generate_invoices')}
                    </button>
                  </div>
                }
              </div>
            )}

            {/* Enhanced Pagination */}
            {lastPage > 1 && (
              <div className="bg-white px-4 py-3 sm:px-6 mt-6 rounded-b-2xl">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    {t('payments.showing')} <span className="font-medium">{pagination.from}</span> {t('payments.to')}{" "}
                    <span className="font-medium">{pagination.to}</span> {t('payments.on')}{" "}
                    <span className="font-medium">{pagination.total}</span> {t('payments.results')}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={pagination.currentPage === 1}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                        // Show first pages, current page with neighbors, or last pages
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-3 py-1 cursor-pointer text-sm rounded-md ${pagination.currentPage === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <button
                          onClick={() => paginate(pagination.lastPage)}
                          className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                        >
                          {pagination.lastPage}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={nextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === pagination.lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {showOzonImporter && (
          <OzonInvoiceImporter onClose={() => setShowOzonImporter(false)} />
        )}

        {/* Modal d'édition */}
        
      </div>
    </div>
  )
}